<template>
  <div class="row">
    <div class="table-footer">
      <div class="page-nav-container flex-h">
        <pagination
          v-model="localPageIndex"
          size="md"
          variant="primary"
          :total-rows="totalCount"
          :per-page="pageSize" />
      </div>
      <div class="page-size-container">
        <multiselect
          v-model="localPageSize"
          name="pageSize"
          track-by="name"
          label="name"
          :placeholder="capitalize(i18n.t('messages.selectItemsPerPage'))"
          :options="pageSizeOptions"
          :show-labels="false"
          :allow-empty="false" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { Multiselect } from 'vue-multiselect';

  import pagination from '@/org-app/components/controls/Pagination.vue';

  interface IPageSizeOption {
    name: string;
    value: number;
  }

  interface ITableFooterProps {
    totalCount: number;
    pageIndex: number;
    pageSize: number;
  }

  const props = withDefaults(defineProps<ITableFooterProps>(), {
    totalCount: 0,
    pageIndex: 1,
    pageSize: 10,
  });

  const emits = defineEmits(['pageIndexChanged', 'pageSizeChanged']);

  const i18n = useI18n();

  const localPageIndex = ref(props.pageIndex);
  const shouldUpdatePage = ref(true);

  const pageSizeOptions = computed((): IPageSizeOption[] =>
    [10, 25, 50, 100].map((x) => {
      return {
        value: x,
        name: `${i18n.t('names.itemPerPage')}`,
      };
    }),
  );

  const localPageSize = computed({
    get(): IPageSizeOption {
      return pageSizeOptions.value.find((x) => x.value === props.pageSize);
    },
    set(option: IPageSizeOption) {
      emits('pageSizeChanged', option.value);
    },
  });

  watch(
    () => props.pageIndex,
    (value) => {
      shouldUpdatePage.value = false;
      localPageIndex.value = value;
      shouldUpdatePage.value = true;
    },
  );

  watch(
    () => localPageIndex.value,
    (value) => {
      if (shouldUpdatePage.value) emits('pageIndexChanged', value);
    },
  );
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;
  .table-footer {
    display: flex;
    flex-direction: row;
    width: 100%;
    background: #fff;
    padding: 1.6rem;
    box-shadow: $small-shadow;
    border: solid 0.1rem $borders-light;
    margin: 0 1.6rem;

    div {
      flex-basis: 50%;
    }

    .page-nav-container {
      display: flex;

      .pagination-container {
        flex-basis: 50%;
        display: flex;
        align-items: center;
      }
    }
  }
</style>
